import { getMinPrice } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { FareFamilyPriceFragment, FlightClass } from '@websky/graphql';
import { Props } from './DesktopFlightModalContent';
import { Tabs } from '../../types';
import { getSelectedFares } from '../../../Results/store/selectedFares/selectors';

import { Money } from '../../../Money';

import { getSelectedExchangeVariants } from '@entities/exchange';
import { TabItem } from '@shared/ui';

export const useLastSelectedFlight = () => {
	try {
		const lastSelectedResultFlights = useSelector(getSelectedFares) ?? [];
		if (lastSelectedResultFlights.length) {
			return lastSelectedResultFlights[lastSelectedResultFlights.length - 1];
		}

		const lastSelectedExchangeFlights = useSelector(getSelectedExchangeVariants) ?? [];
		if (lastSelectedExchangeFlights.length) {
			return lastSelectedExchangeFlights[lastSelectedExchangeFlights.length - 1];
		}

		return null;
	} catch {
		return null;
	}
};

export const useDesktopFlightModalContent = (props: Props) => {
	const [active, onSelect] = useState<Tabs>(() => {
		const hasBusinessPrices = props.businessFares?.some(({ prices }) => prices),
			hasEconomyPrices = props.economyFares?.some(({ prices }) => prices),
			hasMilesPrices = props.milesFares?.some(({ prices }) => prices);
		const availableServices: {
			flightClass: FlightClass;
			tab: Tabs;
		}[] = [];

		if (hasEconomyPrices) {
			availableServices.push({
				flightClass: FlightClass.Economy,
				tab: Tabs.EconomyFares
			});
		}

		if (hasBusinessPrices) {
			availableServices.push({
				flightClass: FlightClass.Business,
				tab: Tabs.BusinessFares
			});
		}

		if (hasMilesPrices) {
			availableServices.push({
				flightClass: FlightClass.Miles,
				tab: Tabs.MilesFares
			});
		}

		if (props.preselectedFlightClass) {
			const existService = availableServices.find(
				service => service.flightClass === props.preselectedFlightClass
			);

			if (existService) {
				return existService.tab;
			}
		}

		const lastSelectedFareCategory = props.lastSelectedFare?.category;

		if (!!lastSelectedFareCategory) {
			const existsService = availableServices.find(service => service.flightClass === lastSelectedFareCategory);

			if (existsService) {
				return existsService.tab;
			}
		}

		return availableServices[0]?.tab;
	});

	const [isFareCompareOpen, setFareCompareOpen] = useState(false);

	const openFareCompare = useCallback(() => {
		setFareCompareOpen(true);
	}, [isFareCompareOpen]);

	const closeFareCompare = useCallback(() => {
		setFareCompareOpen(false);
	}, [isFareCompareOpen]);

	const handleEconomyButtonCLick = useCallback(() => {
		onSelect(Tabs.EconomyFares);
	}, [active]);

	const handleBusinessButtonClick = useCallback(() => {
		onSelect(Tabs.BusinessFares);
	}, [active]);

	const handleMilesButtonClick = useCallback(() => {
		onSelect(Tabs.MilesFares);
	}, [active]);

	const handleFlightDetailsButtonClick = useCallback(() => {
		onSelect(Tabs.FlightInfo);
	}, [active]);

	return {
		active,
		handleEconomyButtonCLick,
		handleBusinessButtonClick,
		handleMilesButtonClick,
		handleFlightDetailsButtonClick,
		isFareCompareOpen,
		openFareCompare,
		closeFareCompare
	};
};

export const useDesktopFlightModalMobileTabs = ({
	isEconomyExists,
	isMilesExists,
	isBusinessFaresExists,
	showFareClassTabs,
	isActiveEconomyFares,
	isActiveBusinessFares,
	isActiveMilesFares,
	handleEconomyButtonCLick,
	handleBusinessButtonClick,
	handleMilesButtonClick,
	economyFares,
	businessFares,
	milesFares
}: {
	isEconomyExists: boolean;
	isMilesExists: boolean;
	isBusinessFaresExists: boolean;
	showFareClassTabs: boolean;
	isActiveEconomyFares: boolean;
	isActiveBusinessFares: boolean;
	isActiveMilesFares: boolean;
	handleEconomyButtonCLick: () => void;
	handleBusinessButtonClick: () => void;
	handleMilesButtonClick: () => void;
	economyFares: FareFamilyPriceFragment[];
	businessFares: FareFamilyPriceFragment[];
	milesFares: FareFamilyPriceFragment[];
}) => {
	const { t } = useTranslation('FlightInfoModalContent');

	return useMemo<{
		tabs: TabItem[];
		active: number;
		onChange: (index: number) => void;
	}>(() => {
		const faresCategory: {
			label: string;
			description: React.ReactNode;
			active: boolean;
			onClick: () => void;
		}[] = [];

		const getMoneyElement = (fares: FareFamilyPriceFragment[]) => {
			try {
				// Sabre (DV) exchange has no prices when search exchange variants
				// WEBSKY-7642
				const minPrices = getMinPrice(
					fares.reduce((prices, fare) => [...prices, ...(fare.prices ?? [])], []),
					price => price.price
				);

				return (
					<>
						{t('Checkout:from')} <Money money={minPrices} />
					</>
				);
			} catch (e) {
				return <>&ndash;</>;
			}
		};

		if (showFareClassTabs) {
			if (isEconomyExists) {
				faresCategory.push({
					label: t('Economy'),
					description: getMoneyElement(economyFares),
					active: isActiveEconomyFares,
					onClick: handleEconomyButtonCLick
				});
			}

			if (isBusinessFaresExists) {
				faresCategory.push({
					label: t('Business'),
					description: getMoneyElement(businessFares),
					active: isActiveBusinessFares,
					onClick: handleBusinessButtonClick
				});
			}

			if (isMilesExists) {
				faresCategory.push({
					label: t('Premium'),
					description: getMoneyElement(milesFares),
					active: isActiveMilesFares,
					onClick: handleMilesButtonClick
				});
			}
		}

		return {
			tabs: faresCategory.map(fare => ({
				label: fare.label,
				description: fare.description
			})),
			active: faresCategory.findIndex(fare => fare.active),
			onChange: index => faresCategory[index].onClick()
		};
	}, [
		t,
		isEconomyExists,
		isMilesExists,
		isBusinessFaresExists,
		showFareClassTabs,
		isActiveEconomyFares,
		isActiveBusinessFares,
		isActiveMilesFares,
		handleEconomyButtonCLick,
		handleBusinessButtonClick,
		handleMilesButtonClick,
		economyFares,
		businessFares,
		milesFares
	]);
};
